import React from 'react';
import AntIcon from '@ant-design/icons';

interface IconProps {
  size?: number;
  color?: string;
  style?: React.CSSProperties;
}

// 變頻冷凍主機圖示（室外機：風扇 + 散熱柵 + 腳架）
export const CompressorIcon: React.FC<IconProps> = ({ size = 18, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', ...style }}>
    {/* 頂蓋 */}
    <rect x={2} y={2} width={20} height={2.5} rx={0.8} fill={color} opacity={0.15} stroke={color} strokeWidth={1.2} />
    {/* 主機殼體 */}
    <rect x={3} y={4.5} width={18} height={15} rx={1.5} stroke={color} strokeWidth={1.5} fill="none" />
    {/* 風扇圓圈 */}
    <circle cx={10} cy={12} r={5.5} stroke={color} strokeWidth={1.3} fill="none" />
    {/* 風扇中心 */}
    <circle cx={10} cy={12} r={1.2} fill={color} opacity={0.8} />
    {/* 6 片扇葉（曲線） */}
    <path d="M10 12 C8.5 10.5, 7 8, 5.5 7.5" stroke={color} strokeWidth={1.3} strokeLinecap="round" fill="none" />
    <path d="M10 12 C12 10.8, 14 8.5, 14.5 7" stroke={color} strokeWidth={1.3} strokeLinecap="round" fill="none" />
    <path d="M10 12 C8 11.5, 5 11.5, 4.5 12" stroke={color} strokeWidth={1.3} strokeLinecap="round" fill="none" />
    <path d="M10 12 C12 12.5, 15 12.5, 15.5 12" stroke={color} strokeWidth={1.3} strokeLinecap="round" fill="none" />
    <path d="M10 12 C8.5 13.5, 7 16, 5.5 16.5" stroke={color} strokeWidth={1.3} strokeLinecap="round" fill="none" />
    <path d="M10 12 C12 13.2, 14 15.5, 14.5 17" stroke={color} strokeWidth={1.3} strokeLinecap="round" fill="none" />
    {/* 右側散熱柵 */}
    {[7, 9, 11, 13, 15, 17].map((y) => (
      <line key={y} x1={17} y1={y} x2={20} y2={y} stroke={color} strokeWidth={1} opacity={0.6} />
    ))}
    {/* 底部腳架 */}
    <circle cx={6.5} cy={21} r={1.2} stroke={color} strokeWidth={1.2} fill="none" />
    <circle cx={17.5} cy={21} r={1.2} stroke={color} strokeWidth={1.2} fill="none" />
  </svg>
);

// 蒸發器圖示（散熱鰭片 + 風扇）
export const EvaporatorIcon: React.FC<IconProps> = ({ size = 18, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', ...style }}>
    {/* 鰭片（垂直線） */}
    {[5, 8, 11, 14, 17, 20].map((x) => (
      <line key={x} x1={x} y1={3} x2={x} y2={15} stroke={color} strokeWidth={1.2} opacity={0.5} />
    ))}
    {/* 管路（水平 U 型） */}
    <path d="M3 5h19" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    <path d="M3 10h19" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    <path d="M3 15h19" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    <path d="M3 5v10" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    <path d="M22 5v10" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    {/* 底部風扇符號 */}
    <circle cx={8} cy={20} r={2.5} stroke={color} strokeWidth={1.2} fill="none" />
    <line x1={8} y1={17.5} x2={8} y2={22.5} stroke={color} strokeWidth={0.8} />
    <line x1={5.5} y1={20} x2={10.5} y2={20} stroke={color} strokeWidth={0.8} />
    <circle cx={17} cy={20} r={2.5} stroke={color} strokeWidth={1.2} fill="none" />
    <line x1={17} y1={17.5} x2={17} y2={22.5} stroke={color} strokeWidth={0.8} />
    <line x1={14.5} y1={20} x2={19.5} y2={20} stroke={color} strokeWidth={0.8} />
  </svg>
);

// 冷藏庫圖示（冷藏室：雪花 + 門 + 控制面板）
export const ColdStorageIcon: React.FC<IconProps> = ({ size = 18, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', ...style }}>
    {/* 外框 */}
    <rect x={2} y={2} width={20} height={20} rx={1.5} stroke={color} strokeWidth={1.8} fill="none" />
    {/* 內框（庫房內牆） */}
    <rect x={3.5} y={3.5} width={17} height={17} rx={0.5} stroke={color} strokeWidth={0.8} opacity={0.3} fill={`${color}08`} />
    {/* 上方色帶 */}
    <rect x={2} y={2} width={20} height={2.5} rx={1.5} fill={color} opacity={0.12} />
    {/* 雪花（六角星） */}
    <g transform="translate(8,7.5)" stroke={color} strokeWidth={1.2} strokeLinecap="round">
      <line x1={0} y1={-3} x2={0} y2={3} />
      <line x1={-2.6} y1={-1.5} x2={2.6} y2={1.5} />
      <line x1={-2.6} y1={1.5} x2={2.6} y2={-1.5} />
      {/* 小分叉 */}
      <line x1={0} y1={-3} x2={-0.8} y2={-2} opacity={0.7} />
      <line x1={0} y1={-3} x2={0.8} y2={-2} opacity={0.7} />
      <line x1={0} y1={3} x2={-0.8} y2={2} opacity={0.7} />
      <line x1={0} y1={3} x2={0.8} y2={2} opacity={0.7} />
    </g>
    {/* 控制面板 */}
    <rect x={16} y={5} width={3.5} height={3} rx={0.5} stroke={color} strokeWidth={1} fill={`${color}10`} />
    <line x1={17} y1={6.2} x2={18.5} y2={6.2} stroke={color} strokeWidth={0.6} opacity={0.6} />
    <line x1={17} y1={7} x2={18.5} y2={7} stroke={color} strokeWidth={0.6} opacity={0.6} />
    {/* 庫門 */}
    <rect x={8} y={12} width={7} height={9} rx={0.8} stroke={color} strokeWidth={1.3} fill={`${color}06`} />
    {/* 門把手 */}
    <line x1={14} y1={15.5} x2={14} y2={18} stroke={color} strokeWidth={1.3} strokeLinecap="round" />
  </svg>
);

// 選單專用：以 AntIcon 包裝（1em + currentColor），自動取得 .ant-menu-item-icon 對齊 margin，
// 與其他選單 icon 等寬對齊（避免裸 svg 文字偏左）。
const ColdStorageMenuSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none">
    <rect x={2} y={2} width={20} height={20} rx={1.5} stroke="currentColor" strokeWidth={1.8} fill="none" />
    <rect x={3.5} y={3.5} width={17} height={17} rx={0.5} stroke="currentColor" strokeWidth={0.8} opacity={0.3} />
    <rect x={2} y={2} width={20} height={2.5} rx={1.5} fill="currentColor" opacity={0.12} />
    <g transform="translate(8,7.5)" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round">
      <line x1={0} y1={-3} x2={0} y2={3} />
      <line x1={-2.6} y1={-1.5} x2={2.6} y2={1.5} />
      <line x1={-2.6} y1={1.5} x2={2.6} y2={-1.5} />
    </g>
    <rect x={16} y={5} width={3.5} height={3} rx={0.5} stroke="currentColor" strokeWidth={1} opacity={0.6} />
    <rect x={8} y={12} width={7} height={9} rx={0.8} stroke="currentColor" strokeWidth={1.3} />
    <line x1={14} y1={15.5} x2={14} y2={18} stroke="currentColor" strokeWidth={1.3} strokeLinecap="round" />
  </svg>
);
export const ColdStorageMenuIcon: React.FC = (props) => <AntIcon component={ColdStorageMenuSvg} {...props} />;

// IOT627 控制器圖示（面板 + 數字）
// 乾淨雪花（代表冷藏；無文字、6 軸對稱）
export const SnowflakeIcon: React.FC<IconProps> = ({ size = 18, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
       style={{ verticalAlign: 'middle', ...style }}>
    {/* 三條主軸 */}
    <line x1={12} y1={2.5} x2={12} y2={21.5} />
    <line x1={3.77} y1={16.75} x2={20.23} y2={7.25} />
    <line x1={20.23} y1={16.75} x2={3.77} y2={7.25} />
    {/* 六端小分叉 */}
    <path d="M12 5 l-1.9 -1.1 M12 5 l1.9 -1.1" />
    <path d="M12 19 l-1.9 1.1 M12 19 l1.9 1.1" />
    <path d="M5.5 8.4 l-2.2 0.2 M5.5 8.4 l0.4 -2.1" />
    <path d="M18.5 15.6 l2.2 -0.2 M18.5 15.6 l-0.4 2.1" />
    <path d="M5.5 15.6 l-2.2 -0.2 M5.5 15.6 l0.4 2.1" />
    <path d="M18.5 8.4 l2.2 0.2 M18.5 8.4 l-0.4 -2.1" />
  </svg>
);

export const ControllerIcon: React.FC<IconProps> = ({ size = 18, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', ...style }}>
    <rect x={3} y={4} width={18} height={16} rx={2} stroke={color} strokeWidth={2} />
    <rect x={5} y={6} width={14} height={8} rx={1} stroke={color} strokeWidth={1} fill={`${color}10`} />
    <circle cx={8} cy={18} r={1} fill={color} />
    <circle cx={12} cy={18} r={1} fill={color} />
    <circle cx={16} cy={18} r={1} fill={color} />
    <text x={12} y={12} textAnchor="middle" fill={color} fontSize={6} fontWeight={700}>T°</text>
  </svg>
);

// AI 控箱圖示（電路板/晶片）
export const AIBoxIcon: React.FC<IconProps> = ({ size = 18, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', ...style }}>
    <rect x={6} y={6} width={12} height={12} rx={2} stroke={color} strokeWidth={2} />
    {/* 接腳 */}
    <line x1={9} y1={4} x2={9} y2={6} stroke={color} strokeWidth={1.5} />
    <line x1={12} y1={4} x2={12} y2={6} stroke={color} strokeWidth={1.5} />
    <line x1={15} y1={4} x2={15} y2={6} stroke={color} strokeWidth={1.5} />
    <line x1={9} y1={18} x2={9} y2={20} stroke={color} strokeWidth={1.5} />
    <line x1={12} y1={18} x2={12} y2={20} stroke={color} strokeWidth={1.5} />
    <line x1={15} y1={18} x2={15} y2={20} stroke={color} strokeWidth={1.5} />
    <line x1={4} y1={9} x2={6} y2={9} stroke={color} strokeWidth={1.5} />
    <line x1={4} y1={12} x2={6} y2={12} stroke={color} strokeWidth={1.5} />
    <line x1={4} y1={15} x2={6} y2={15} stroke={color} strokeWidth={1.5} />
    <line x1={18} y1={9} x2={20} y2={9} stroke={color} strokeWidth={1.5} />
    <line x1={18} y1={12} x2={20} y2={12} stroke={color} strokeWidth={1.5} />
    <line x1={18} y1={15} x2={20} y2={15} stroke={color} strokeWidth={1.5} />
    <text x={12} y={14} textAnchor="middle" fill={color} fontSize={6} fontWeight={700}>AI</text>
  </svg>
);

// 溫度計圖示
export const ThermometerIcon: React.FC<IconProps> = ({ size = 18, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle', ...style }}>
    <path d="M14 14.76V3.5a2 2 0 0 0-4 0v11.26a4.5 4.5 0 1 0 4 0Z" stroke={color} strokeWidth={2} fill="none" />
    <circle cx={12} cy={18} r={2} fill={color} opacity={0.7} />
    <line x1={12} y1={8} x2={12} y2={16} stroke={color} strokeWidth={2} strokeLinecap="round" />
  </svg>
);

// 振動感測器圖示（波形）— 使用 Ant Design Icon 元件確保 Menu 對齊
const VibrationSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none">
    <path d="M2 12h3l2-6 3 12 3-12 2 6h3" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <circle cx={20} cy={12} r={2} stroke="currentColor" strokeWidth={1.5} fill="none" />
  </svg>
);

export const VibrationIcon: React.FC = (props) => <AntIcon component={VibrationSvg} {...props} />;
